/**
 * Created by piotr.pozniak@thebeaverhead.com on 09/08/2019.
 */

import React, { useCallback, useContext, useMemo, useState } from "react";

import classnames from "classnames";
import DetailsRow from "./DetailsRow";
import EventCTA from "./EventCTA";
import PropTypes from "prop-types";
import Forms from "./Forms";
import EventImage from "./EventImage";
import PoweredBy from "../PoweredBy";
import { useCalendarStore } from "../../../../hooks/redux/calendar";
import EventBadges from "../EventBadges";
import EventSchedule from "./EventSchedule";
import RecurrenceSeries from "./RecurrenceSeries";
import EventDetailsTime from "./EventDetailsTime";
import WidgetSettingsContext from "../../../../contexts/WidgetSettingsContext";
import ShareButton from "./ShareButton";
import EventPlace from "./EventPlace";
import EventDescription from "./EventDescription";
import { useTranslation } from "react-i18next";
import SubscribeButton from "./SubscribeButton";
import EventDetailsLocation from "./EventDetailsLocation";
import { useScrollHeightObserver } from "../../../../hooks/useScrollHeightObserver";
import {
  getPublicWidgetUrl,
  getUploadResourceUrl,
} from "../../../../helpers/url";

const EventDetails = React.memo((props) => {
  const calendarSettingsContext = useContext(WidgetSettingsContext);

  const { t } = useTranslation();
  const [innerContainerHeight, setInnerContainerHeight] = useState(0);
  const [numberOfNextEvents, setNumberOfNextEvents] = useState(3);

  const { calendar } = useCalendarStore();
  const event = props.event;

  const [detailsContainerRef, scrollHeight] = useScrollHeightObserver();

  /**
   *
   * @param e
   */
  const updateInnerContainerHeightWithImage = (e) => {
    setInnerContainerHeight(innerContainerHeight + e.target.scrollHeight);
  };

  const calendarSlug = props.calendarSlug;

  const formsData =
    event.forms && !Array.isArray(event.forms)
      ? [event.forms]
      : event.forms && Array.isArray(event.forms)
      ? event.forms
      : null;

  const hasForms = formsData && formsData.length ? true : false;

  const startDate = useMemo(
    () => new Date(event.start_time * 1000) /*.utc()*/,
    [event.start_time]
  );

  const endDate = useMemo(
    () => new Date(event.end_time * 1000) /*.utc()*/,
    [event.end_time]
  );

  const showForms =
    Number.parseInt(calendarSettingsContext["formShowForms"]) === 1;

  const showFormDescription =
    Number.parseInt(calendarSettingsContext["formShowDescription"]) === 1;

  const showLocation =
    Number.parseInt(calendarSettingsContext["eventDetailsShowLocation"]) == 1;

  const showImage =
    // Number.parseInt(calendarSettingsContext["eventDetailsShowImage"]) == 1;
    Number.parseInt(calendarSettingsContext["eventDetailsShowImage"]) == 1;
  const imageAspectRatio =
    calendarSettingsContext["eventDetailsImageAspectRatio"];
  const showOrganizer =
    Number.parseInt(calendarSettingsContext["eventDetailsShowOrganizer"]) === 1;
  const showOrganizerEmail =
    Number.parseInt(
      calendarSettingsContext["eventDetailsShowOrganizerEmail"]
    ) === 1;

  const showShareOptions =
    Number.parseInt(calendarSettingsContext["eventDetailsShowShareOptions"]) ===
    1;

  const showRecurrenceDescription =
    Number.parseInt(
      calendarSettingsContext["eventDetailsShowRepeatingEventsTimes"]
    ) === 1 && event.recurrence_description;

  const showRecurringEventSeries =
    Number.parseInt(
      calendarSettingsContext["eventDetailsShowRecurringSeries"]
    ) === 1 && event.recurrence_description;

  const showAddToCalendar =
    Number.parseInt(
      calendarSettingsContext["eventDetailsShowSubscribeOptions"]
    ) === 1;

  const showEventTime =
    Number.parseInt(calendarSettingsContext["eventDetailsShowTime"]) === 1;

  const showEventSchedule =
    Number.parseInt(calendarSettingsContext["eventDetailsShowSchedule"]) === 1;

  const showEventDescription =
    Number.parseInt(calendarSettingsContext["generalShowDescription"]) === 1;

  const showShortSummary =
    Number.parseInt(calendarSettingsContext["generalShowShortSummary"]) === 1;

  const recurringShowBadge =
    Number.parseInt(calendarSettingsContext["recurringShowBadgeInDetails"]) ===
    1;

  const featuredShowBadge =
    Number.parseInt(calendarSettingsContext["featuredShowBadgeInDetails"]) ===
    1;

  const badgesLocation = calendarSettingsContext["recurringBadgeLocation"];

  const showPrimaryCta =
    Number.parseInt(calendarSettingsContext["cPopupShowCta1st"]) === 1;

  const hideDescriptionCtaDuplicateLinks =
    Number.parseInt(
      calendarSettingsContext["eventDetailsHideDescriptionCtaLinks"]
    ) === 1;

  // Mirrors the resolution EventCTA does internally for settingsPrefix="cPopup" -
  // needed here too so EventDescription knows which URL(s) to strip.
  const primaryCtaAction = calendarSettingsContext["cPopupCTAAction1st"];
  const primaryCtaLink =
    event.ctas && primaryCtaAction
      ? event.ctas[primaryCtaAction] || null
      : null;

  const showBranding =
    Number.parseInt(calendarSettingsContext["eventDetailsShowBranding"]) ===
      1 && !!calendar?.model?.organization?.settings?.logo?.trim();

  const showEventTimeOption =
    calendarSettingsContext["eventDetailsShowTimeOptions"];

  const forms =
    showForms && hasForms ? (
      <Forms forms={formsData} showDescription={showFormDescription} />
    ) : null;

  const eventCTA = showPrimaryCta ? (
    <EventCTA
      event={event}
      className={"dce--event-card-details-item dce--event-card--cta"}
      buttonClassName={"dce--event-card--cta-btn"}
    />
  ) : null;

  const eventUrl = getPublicWidgetUrl(
    `e/${calendarSlug}/${event.slug}`,
    calendar.model.widget_domain
  );

  const organizerName =
    showOrganizerEmail && event.organizer.email != null ? (
      <a
        href={"mailto:" + event.organizer.email}
        className={"event-details--details-anchor anchor "}
      >
        <i className={"material-icons"}>email</i>
        {event.organizer.name}
      </a>
    ) : (
      event.organizer.name
    );

  const shareBlock =
    showShareOptions || showAddToCalendar ? (
      <DetailsRow title={null} icon={null} showBottomBorder={false}>
        <div className="event-row--details-row__share-subscribe">
          {showShareOptions ? (
            <ShareButton
              url={eventUrl}
              title={event.name}
              text={event.description}
            />
          ) : null}
          {showAddToCalendar ? <SubscribeButton /> : null}
        </div>
      </DetailsRow>
    ) : null;

  const brandingBlock = showBranding ? (
    <DetailsRow title={null} icon={null}>
      <div className="event-row--details-row__branding">
        <img
          src={`${getUploadResourceUrl(
            calendar.model.organization.settings.logo
          )}`}
        />
      </div>
    </DetailsRow>
  ) : null;

  const onShowMoreRecurringEvents = useCallback(() => {
    setNumberOfNextEvents(numberOfNextEvents + 3);
  }, [numberOfNextEvents]);

  const recurrenceSeries =
    showRecurrenceDescription &&
    event.recurrence_description /*event.recurrence_series */ ? (
      <RecurrenceSeries
        series={event.recurrence_series}
        eventId={event.slug}
        eventName={event.name}
        description={event.recurrence_description}
        numberOfNextEvents={numberOfNextEvents}
        onShowMore={onShowMoreRecurringEvents}
        showSeries={showRecurringEventSeries ? true : false}
        isAllDayEvent={event.allDay}
        eventStartDate={startDate}
      />
    ) : null;

  const eventImageBlock =
    showImage && event.cover_image ? (
      <EventImage
        src={event.cover_image}
        aspectRatio={imageAspectRatio}
        onLoad={updateInnerContainerHeightWithImage}
      />
    ) : null;

  const organizerBlock =
    showOrganizer && organizerName ? (
      <DetailsRow title={t("Organizer")} icon="headset">
        <div className="">
          <h4>{organizerName}</h4>
        </div>
      </DetailsRow>
    ) : null;

  const embeddedSingleEvent = window.location.pathname.match(
    `/e/.*/${props.expandedRowSlug}`
  )
    ? true
    : false;

  const watermark =
    calendar.model &&
    Number.parseInt(calendar.model.is_watermarked) === 1 &&
    !embeddedSingleEvent ? (
      <div className={"event-row--details"}>
        <div className="event-row--details-row">
          <div className="col-content ">
            <PoweredBy />
          </div>
        </div>
      </div>
    ) : null;

  const eventDateTime = showEventTime ? (
    <div
      className={classnames("col-content", {
        "col-content--right": showLocation,
      })}
    >
      <div className="col-icon">
        <i className="material-icons">schedule</i>
      </div>

      <div>
        <h3 className="">{t("Time of the event")}</h3>
        <EventDetailsTime
          startDate={startDate}
          endDate={endDate}
          isAllDayEvent={event.allDay}
          showEventTimeOption={showEventTimeOption}
        />
      </div>
    </div>
  ) : null;

  const eventSchedule = showEventSchedule ? (
    <EventSchedule eventId={event.slug} schedule={event.eventTimes} />
  ) : null;

  const eventBadges =
    (recurringShowBadge && event.recurrence_description) ||
    (featuredShowBadge && event.is_featured) ? (
      <EventBadges
        location={badgesLocation}
        showRecurringBadge={recurringShowBadge}
        showFeaturedBadge={featuredShowBadge && event.is_featured}
      />
    ) : null;

  const eventShortSummary =
    event.summary && event.summary.length && showShortSummary ? (
      <DetailsRow title={t("Event summary")} icon="reorder">
        <div className="">
          <p
            className={"p"}
            dangerouslySetInnerHTML={{ __html: event.summary }}
          />
        </div>
      </DetailsRow>
    ) : null;

  return (
    <div
      ref={detailsContainerRef}
      id={"event-details-" + event.slug}
      style={{
        maxHeight:
          props.showDetails || props.expandedRowSlug === event.slug
            ? scrollHeight + "px"
            : 0,
      }}
      className={classnames("event-details--wrapper")}
    >
      {eventImageBlock}
      {eventBadges}
      {eventShortSummary}

      {showEventDescription && event.description && event.description.length ? (
        <EventDescription
          event={event}
          hideDuplicateLinks={
            hideDescriptionCtaDuplicateLinks &&
            showPrimaryCta &&
            !!primaryCtaLink
          }
          duplicateLinkUrls={primaryCtaLink ? [primaryCtaLink.url] : []}
        />
      ) : null}

      {eventCTA}

      <div className={"event-row--details"}>
        <div className="event-row--details-row-doubled">
          <div className={"event-row--details-row-doubled--inner-wrapper"}>
            {eventDateTime}

            <EventDetailsLocation
              location={event.location}
              displayLocation={event.display_location}
            />
          </div>
        </div>
      </div>
      <EventPlace place={event.place} />
      {eventSchedule}

      {forms}

      {organizerBlock}

      {recurrenceSeries}

      {shareBlock}
      {brandingBlock}

      {watermark}
    </div>
  );
});

EventDetails.defaultProps = {
  showDetails: false,
};

EventDetails.propTypes = {
  showDetails: PropTypes.bool,
  calendarSlug: PropTypes.string,
  embedded: PropTypes.bool,

  expandedRowSlug: PropTypes.string,
};
EventDetails.whyDidYouRender = true;

export default EventDetails;
